import { useCallback, useEffect, useRef, useState } from 'react'
import { useRefreshSignal } from '@/features/realtime/useRefreshSignal'
import { refreshCoordinator } from '@/features/realtime/refreshCoordinator'
import { tasksApi } from '@/api'
import { callApi } from '@/api/callApi'
import { ownerActivityApi, OWNER_PORTAL_PERMS } from '@/api/ownerPortalApi'
import { usePermission } from '@/hooks/usePermission'
import { normalizePath } from './nav-model'


/** 注册表 key 与 AppSidebar 查表时同一套 normalizePath,杜绝斜杠约定漂移。 */
export const MY_TASKS_BADGE_KEY = normalizePath('/my-tasks')
export const VOICEMAIL_BADGE_KEY = normalizePath('/call-logs')
/** 员工端 Owner Activity：员工未读的业主留言数（2026-10-30 起这是员工获知业主留言的唯一入口） */
export const OWNER_ACTIVITY_BADGE_KEY = normalizePath('/owner-activity')

/**
 * My Tasks badge 口径:assign 给当前用户且非终态的 task。
 * CURRENT_USER_ID 由后端按请求用户动态替换;DSL string 没有 not_in,正向列举
 * 非终态(⚠️ 后端新增 status 值时需同步这里)。
 */
const MY_OPEN_TASKS_FILTERS = {
  assigneeId: { type: 'string', operator: 'equals', value: 'CURRENT_USER_ID' },
  status: { type: 'string', operator: 'in', value: ['open', 'in_progress', 'on_hold'] },
}

/** Task, voicemail and owner activity counts: initial load, shared refresh notifications and reconciliation. */
export function useMenuBadges(
  hasMyTasksMenu: boolean,
  hasCallLogsMenu: boolean,
  hasOwnerActivityMenu = false,
): Record<string, number | undefined> {
  const [counts, setCounts] = useState<Record<string, number | undefined>>({})
  const generation = useRef(0)
  const taskRequest = useRef(0)
  const voicemailRequest = useRef(0)
  const ownerActivityRequest = useRef(0)

  // GET /calls/voicemail-count requires the `call:read` permission — having the
  // Call Logs MENU item is not the same as holding the permission. Without this
  // gate, a visible menu alone would allow repeated unauthorized snapshot reads.
  const { hasPermission } = usePermission()
  const canReadVoicemails = hasCallLogsMenu && hasPermission('call:read')
  // 同理：Owner Activity 菜单在、且有 owner_portal:report:read 才读未读数
  const canReadOwnerActivity = hasOwnerActivityMenu && hasPermission(OWNER_PORTAL_PERMS.reportRead)

  const fetchMyTasks = useCallback(async () => {
    const epoch = generation.current
    const request = ++taskRequest.current
    try {
      const res: any = await tasksApi.searchTask({
        page: 1,
        pageSize: 1,
        filters: MY_OPEN_TASKS_FILTERS,
      })
      if (epoch !== generation.current || request !== taskRequest.current) return
      const total = res?.pagination?.totalResults
      if (typeof total === 'number') {
        setCounts(prev =>
          prev[MY_TASKS_BADGE_KEY] === total ? prev : { ...prev, [MY_TASKS_BADGE_KEY]: total })
      } else {
        // 契约漂移诊断:shape 不对时 dump,方便直接拿日志找后端
        console.warn('[useMenuBadges] unexpected searchTask shape:', res)
      }
    } catch (e) {
      console.warn('[useMenuBadges] my-tasks count fetch failed:', e)
      return false
    }
  }, [])

  const fetchVoicemails = useCallback(async () => {
    const epoch = generation.current
    const request = ++voicemailRequest.current
    try {
      const count = await callApi.getVoicemailCount()
      if (epoch !== generation.current || request !== voicemailRequest.current) return
      setCounts(prev =>
        prev[VOICEMAIL_BADGE_KEY] === count ? prev : { ...prev, [VOICEMAIL_BADGE_KEY]: count })
    } catch (e) {
      // 失败静默:保留既有数字,返回 false 供刷新诊断记录
      console.warn('[useMenuBadges] voicemail count fetch failed:', e)
      return false
    }
  }, [])

  const fetchOwnerActivity = useCallback(async () => {
    const epoch = generation.current
    const request = ++ownerActivityRequest.current
    try {
      const res = await ownerActivityApi.unreadCount()
      if (epoch !== generation.current || request !== ownerActivityRequest.current) return
      const count = res?.unreadMessages
      if (typeof count === 'number') {
        setCounts(prev =>
          prev[OWNER_ACTIVITY_BADGE_KEY] === count ? prev : { ...prev, [OWNER_ACTIVITY_BADGE_KEY]: count })
      } else {
        console.warn('[useMenuBadges] unexpected owner activity unread shape:', res)
      }
    } catch (e) {
      // 失败静默:保留既有数字(旧后端没有这个端点时 404 也走这里),返回 false 供刷新诊断记录
      console.warn('[useMenuBadges] owner activity count fetch failed:', e)
      return false
    }
  }, [])

  useRefreshSignal({ resource: 'myTasks', enabled: hasMyTasksMenu, onRefresh: fetchMyTasks })
  useRefreshSignal({ resource: 'voicemail', enabled: canReadVoicemails, onRefresh: fetchVoicemails })
  useRefreshSignal({ resource: 'ownerActivity', enabled: canReadOwnerActivity, onRefresh: fetchOwnerActivity })
  useEffect(() => {
    if (hasMyTasksMenu) void fetchMyTasks()
    if (canReadVoicemails) void fetchVoicemails()
    if (canReadOwnerActivity) void fetchOwnerActivity()
    const logout = () => { generation.current++; setCounts({}) }
    window.addEventListener('auth:unauthorized', logout)
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps -- Generation counter, not a DOM ref; invalidate all outstanding reads.
      generation.current++
      window.removeEventListener('auth:unauthorized', logout)
    }
  }, [hasMyTasksMenu, canReadVoicemails, canReadOwnerActivity, fetchMyTasks, fetchVoicemails, fetchOwnerActivity])

  // 语音留言被标记已解决时（call-logs 页 dispatch 的自定义事件）立刻刷新计数，
  // 与远端通知走同一个合并调度入口。
  useEffect(() => {
    if (!canReadVoicemails) return
    const onResolved = () => { refreshCoordinator.invalidate(['voicemail'], 'local-write') }
    window.addEventListener('voicemail-resolved', onResolved)
    return () => window.removeEventListener('voicemail-resolved', onResolved)
  }, [canReadVoicemails, fetchVoicemails])

  return counts
}
